import { useEffect, useState } from "react";
import type { ConnectionState } from "./AccountConnections";
import type { AccountTab } from "./portal-navigation";
import { parsePhoneMailboxes } from "./VoicemailPanel";
import {
  parseDepartmentMailboxes,
  voicemailCounts,
  type VoicemailApi,
} from "./DepartmentVoicemailPanel";

export type CustomerAccountHomeProps = {
  accountId: string;
  account: { name: string; kind: string };
  numbers: { id: string; e164: string; status?: string }[];
  devices: { id: string; model: string; label?: string; status?: string }[];
  connections: ConnectionState;
  api: VoicemailApi;
  refreshVersion: number;
  onTab: (tab: AccountTab) => void;
  onHelp?: (() => void) | undefined;
  readOnly?: boolean;
};

type Counts = { mailboxes: number; newMessages: number; savedMessages: number };
type CountState = {
  scope: string;
  loading: boolean;
  counts: Counts | null;
  checkedAt: number | null;
  error: string;
};
const empty = (scope: string): CountState => ({
  scope,
  loading: false,
  counts: null,
  checkedAt: null,
  error: "",
});
const phoneCounts = (value: unknown): Counts => {
  const { items } = parsePhoneMailboxes(value);
  for (const box of items) {
    const counts = voicemailCounts(box.messages);
    if (counts.new !== box.newMessages || counts.saved !== box.oldMessages)
      throw new Error(
        "The voicemail counts do not match the message list. Refresh to check again.",
      );
  }
  return {
    mailboxes: items.length,
    newMessages: items.reduce((total, box) => total + box.newMessages, 0),
    savedMessages: items.reduce((total, box) => total + box.oldMessages, 0),
  };
};
const departmentCounts = (value: unknown): Counts => {
  const boxes = parseDepartmentMailboxes(value);
  return boxes.reduce(
    (total, box) => {
      const counts = voicemailCounts(box.messages);
      return {
        mailboxes: total.mailboxes + 1,
        newMessages: total.newMessages + counts.new,
        savedMessages: total.savedMessages + counts.saved,
      };
    },
    { mailboxes: 0, newMessages: 0, savedMessages: 0 },
  );
};

async function readMailboxCounts(
  api: VoicemailApi,
  path: string,
  signal: AbortSignal,
) {
  const request = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => {
      request.abort();
      reject(new Error("Voicemail check canceled."));
    };
    if (signal.aborted) cancel();
    else {
      signal.addEventListener("abort", cancel, { once: true });
      timeout = setTimeout(() => {
        request.abort();
        reject(new Error("The voicemail check timed out. Try refreshing."));
      }, 10_000);
    }
  });
  try {
    return await Promise.race([
      Promise.resolve().then(() => {
        if (request.signal.aborted)
          throw new Error("Voicemail check canceled.");
        return api<unknown>(path, { signal: request.signal });
      }),
      interrupted,
    ]);
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", cancel);
  }
}

function useMailboxCounts(
  api: VoicemailApi,
  path: string,
  parse: (value: unknown) => Counts,
  refreshVersion: number,
  manualRefresh: number,
) {
  const [state, setState] = useState<CountState>(() => empty(path));
  useEffect(() => {
    const lifecycle = new AbortController();
    let busy = false;
    const refresh = async () => {
      if (busy || lifecycle.signal.aborted) return;
      busy = true;
      setState((previous) => ({
        ...(previous.scope === path ? previous : empty(path)),
        loading: true,
      }));
      try {
        const value = await readMailboxCounts(api, path, lifecycle.signal);
        const counts = parse(value);
        if (!lifecycle.signal.aborted)
          setState({
            scope: path,
            counts,
            checkedAt: Date.now(),
            loading: false,
            error: "",
          });
      } catch (cause) {
        if (!lifecycle.signal.aborted)
          setState((previous) => ({
            ...(previous.scope === path ? previous : empty(path)),
            loading: false,
            error: (cause as Error).message,
          }));
      } finally {
        busy = false;
      }
    };
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const timer = setInterval(onVisible, 30_000);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      lifecycle.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, path, parse, refreshVersion, manualRefresh]);
  return state.scope === path ? state : empty(path);
}

function CountRow({
  title,
  state,
  now,
}: {
  title: string;
  state: CountState;
  now: number;
}) {
  const age = state.checkedAt === null ? NaN : now - state.checkedAt;
  const fresh =
    Number.isFinite(age) &&
    age >= -5_000 &&
    age < 60_000 &&
    !state.error &&
    !state.loading;
  return (
    <div className="cr-home-mailbox">
      <dt>{title}</dt>
      <dd>
        {state.counts ? (
          <>
            <strong>
              {!fresh ? "Last check: " : ""}
              {state.counts.mailboxes
                ? `${state.counts.newMessages} new message${state.counts.newMessages === 1 ? "" : "s"}`
                : "No mailbox installed"}
            </strong>
            {state.checkedAt !== null && (
              <small>
                Checked {new Date(state.checkedAt).toLocaleTimeString()}
              </small>
            )}
          </>
        ) : (
          <strong>
            {state.error ? "Count unavailable" : "Checking messages…"}
          </strong>
        )}
        {state.loading && state.counts && (
          <small>Checking for new messages…</small>
        )}
        {state.error && (
          <small className="cr-home-mailbox-error" role="status">
            Current count unavailable. {state.error}
          </small>
        )}
      </dd>
    </div>
  );
}

export function customerPhoneStatus(connections: ConnectionState, now: number) {
  if (connections.error) return "Phone status unavailable";
  if (!connections.data) return "Checking phone connection…";
  const lines = connections.data.lines.filter(
    (line) => line.status !== "disconnected",
  );
  if (!lines.length) return "Phone setup is not complete";
  const current = lines.filter((line) => {
    const checked = line.observation?.checkedAt
      ? Date.parse(line.observation.checkedAt)
      : NaN;
    return (
      line.observation?.freshness === "current" &&
      now - checked >= -5000 &&
      now - checked <= 60_000
    );
  });
  const connected = current.filter(
    (line) => line.observation?.registration === "registered",
  ).length;
  if (connected)
    return `${connected} of ${lines.length} phone${lines.length === 1 ? "" : "s"} connected`;
  if (current.some((line) => line.observation?.registration === "offline"))
    return "Phone connection needs attention";
  if (
    current.some((line) => line.observation?.registration === "not_configured")
  )
    return "Phone setup is not complete";
  return "Phone status unavailable";
}

const phoneNumber = (value: string) =>
  /^\+1\d{10}$/.test(value)
    ? `(${value.slice(2, 5)}) ${value.slice(5, 8)}-${value.slice(8)}`
    : value;

export function CustomerAccountHome({
  accountId,
  account,
  numbers,
  connections,
  api,
  refreshVersion,
  onTab,
  onHelp,
  readOnly = false,
}: CustomerAccountHomeProps) {
  const [manualRefresh, setManualRefresh] = useState(0);
  const [now, setNow] = useState(Date.now);
  const base = `/customer/accounts/${accountId}`;
  const phone = useMailboxCounts(
    api,
    `${base}/voicemail`,
    phoneCounts,
    refreshVersion,
    manualRefresh,
  );
  const departments = useMailboxCounts(
    api,
    `${base}/ivrs`,
    departmentCounts,
    refreshVersion,
    manualRefresh,
  );
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, 15_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  const currentNumbers = numbers.filter(
    (number) => !["released", "aging", "failed"].includes(number.status ?? ""),
  );
  return (
    <section
      className="cr-panel cr-wide cr-customer-home"
      aria-label="Everyday telephone tasks"
    >
      <div className="cr-home-service">
        <div>
          <h3>Your telephone service</h3>
          <p className="cr-home-numbers">
            {currentNumbers.length
              ? currentNumbers
                  .map((number) => phoneNumber(number.e164))
                  .join(" · ")
              : "Your telephone number is still being set up."}
          </p>
          <p>
            {customerPhoneStatus(connections, now)}. Registration does not
            verify call audio.
          </p>
        </div>
        <div className="cr-customer-tasks">
          <button className="cr-button" onClick={() => onTab("voicemail")}>
            {readOnly ? "Review voicemail" : "Listen to voicemail"}
          </button>
          <button
            className="cr-button secondary"
            onClick={() => onTab("features")}
          >
            {readOnly
              ? "Review calling features"
              : "Call forwarding & features"}
          </button>
          <button
            className="cr-button secondary"
            onClick={() => onTab("devices")}
          >
            Equipment & setup
          </button>
          {onHelp && (
            <button className="cr-button secondary" onClick={onHelp}>
              Get help
            </button>
          )}
        </div>
      </div>
      <div className="cr-home-voicemail" aria-label="Voicemail summary">
        <div className="cr-account-heading">
          <h4>Voicemail waiting</h4>
          <button
            className="cr-text-button"
            disabled={phone.loading || departments.loading}
            onClick={() => setManualRefresh((value) => value + 1)}
          >
            Refresh voicemail counts
          </button>
        </div>
        <dl className="cr-home-mailbox-counts">
          <CountRow title="Phone voicemail" state={phone} now={now} />
          {(account.kind !== "residential" ||
            !departments.counts ||
            departments.counts.mailboxes > 0 ||
            departments.error) && (
            <CountRow
              title="Department voicemail"
              state={departments}
              now={now}
            />
          )}
        </dl>
        <p className="cr-form-note">
          New and Saved are phone-server folders. Listening in the portal does
          not move a message to Saved. Counts refresh every 30 seconds while
          this page is visible.
        </p>
      </div>
      {readOnly && (
        <p className="cr-form-note">
          Read-only customer view. Exit to change settings or open private
          recordings.
        </p>
      )}
    </section>
  );
}
